// Maneuver program for the "Invatare" (learning) module.
//
// Where the training module just drops the trainee in front of the station and
// scores the outcome, the learning module walks them through the exercise the
// way a real dispatch does (Documentation_Modules/learning_ro.txt):
//
//   1. D.E.I. dictates the operative state one cell must reach   -> `dispatcher`
//      ("Adu in stare deconectata LEA SRA OE1, celula 4K")
//   2. the trainee repeats it back out loud                      -> `confirm`
//   3. the trainee operates the switches that state needs        -> `ops`
//   4. the trainee reports the result back                       -> `report`
//   5. D.E.I. acknowledges it                                    -> `ack`
//
// Every step also produces the rows it occupies in the "foaie de manevra"
// (execution maneuver sheet), so the sheet, the dictation and the scheme all
// stay in lockstep.

import {
    SRS_CELLS, STATE_LABEL, STATE_RULE, StateClass, buildConfig, stateClassOf, stateLabelOf, scopeLabel,
    stateDepthOf, stateJourney, wantBarSepClosed, wantGenOn,
} from "./htScenarios";
import type { CellSwitches } from "./srsScheme";

export type SwitchType = "barSep" | "lineSep" | "breaker" | "clp" | "generator";
export type StepKind = "order" | "announce";

export interface StepSwitch {
    type: SwitchType;
    bar?: "I" | "II";
    barLine?: string;               // IB / IIA ... (bar separators only)
    cell: [number, number];         // grid coords of the switch
    action: "close" | "open" | "start" | "stop";
}

export interface LearnStep {
    id: string;
    number: string;                 // "1"
    kind: StepKind;
    cellId?: string;
    dispatcher: string;             // what D.E.I. says (spoken through TTS)
    confirm: string;                // what the trainee must repeat back
    report?: string;                // what the trainee reports once it is done
    ack?: string;                   // D.E.I.'s acknowledgement of that report
    fileText: string;               // the maneuver-sheet row of the order itself
    ops: StepSwitch[];              // switches to operate, in safe order
    // Which rung of the operative-state ladder this step belongs to. The whole
    // exercise is a chain of state-to-state legs ("functiune" -> "stare
    // deconectata" -> ...), and every step is one cell moving on one leg.
    legIdx: number;
    legFrom: string;                // label of the state the leg starts from
    legTo: string;                  // label of the state the leg ends in
}

// A row of the maneuver sheet. `stepId` is the step the row belongs to; op rows
// additionally carry `rowId` (their own key in the row-state map) and the index
// of the switch operation they document.
export interface FileRow {
    number: string;
    text: string;
    stepId?: string;
    rowId?: string;
    opIdx?: number;
    header?: boolean;
}

export interface LearnProgram {
    steps: LearnStep[];
    rows: FileRow[];
    theme: string;                  // "Aducerea in <stare> a <celula>"
    purpose: string;
    initialNote: string;            // S.O.I. line
}

// ---- text helpers -----------------------------------------------------------

// "GENERATOR 1 (CEL 20K)" -> { name: "GENERATOR 1", cel: "CEL 20K" }
const splitLabel = (id: string): { name: string, cel: string } => {
    const c = SRS_CELLS.find((x) => x.id === id);
    const label = c?.label || id;
    const m = label.match(/^(.*?)\s*\((.*)\)$/);
    return { name: (m ? m[1] : label).trim(), cel: m ? m[2].trim() : "" };
};

// "celula 20K" / "" when the cell carries no cell code. The label carries it as
// "CEL 20K"; spoken and written orders name the cell as "celula 20K" (see
// Documentation_Modules/learning_ro.txt).
const at = (id: string): string => {
    const { name, cel } = splitLabel(id);
    const code = cel.replace(/^CEL\s*/i, "");
    return code ? `${name}, celula ${code}` : name;
};

// RO noun for a switch, with the busbar name for bar separators.
const nounOf = (sw: StepSwitch): string => {
    switch (sw.type) {
        case "barSep": return `separatorul de bară ${sw.barLine || sw.bar || ""}`.trim();
        case "lineSep": return "separatorul de linie";
        case "breaker": return "întreruptorul";
        case "clp": return "CLP-ul";
        default: return "generatorul";
    }
};
// Genitive form used by the "verify the position of ..." rows.
const nounOfGen = (sw: StepSwitch): string => {
    switch (sw.type) {
        case "barSep": return `separatorului de bară ${sw.barLine || sw.bar || ""}`.trim();
        case "lineSep": return "separatorului de linie";
        case "breaker": return "întreruptorului";
        case "clp": return "CLP-ului";
        default: return "generatorului";
    }
};
// Reflexive, for the sheet ("Se identifica si se deschide ...").
const verbFile = (sw: StepSwitch): string =>
    sw.action === "close" ? "se închide" : sw.action === "open" ? "se deschide" : sw.action === "start" ? "se pornește" : "se oprește";
// Resulting position, for the verification row.
const positionOf = (sw: StepSwitch): string =>
    sw.action === "close" ? "închis" : sw.action === "open" ? "deschis" : sw.action === "start" ? "pornit" : "oprit";

// ---- ordering ---------------------------------------------------------------

// Safe operating-maneuver order inside one bay (same weights the transition
// preview uses): a disconnector is never switched under load, so de-energizing
// runs breaker -> line sep -> bar sep and energizing runs the other way round.
const OP_WEIGHT: Record<string, number> = {
    "generator:stop": -1,
    "clp:open": 0,
    "generator:start": 0.5,
    "breaker:open": 1,
    "lineSep:open": 2,
    "barSep:open": 3,
    "barSep:close": 4,
    "lineSep:close": 5,
    "breaker:close": 6,
    "clp:close": 7,
};

const GENERATOR_IDS = new Set(["GENERATOR 1", "GENERATOR 2"]);

// How far "out of service" each operative state sits. Comparing the depth of
// the start and the target state tells the program which way the exercise runs.
const STATE_DEPTH: Record<StateClass, number> = {
    NORMAL: 0,
    DISCONNECTED: 1,
    VISIBLY_SEPARATED: 2,
    EARTHED: 3,
    ALL_OPEN: 3,
};

/** The switch operations one cell needs to go from `start` to `target`. */
const cellOperations = (
    id: string, meta: CellSwitches, start: StateClass, target: StateClass,
    sepBarLine: Record<string, string>,
): StepSwitch[] => {
    const a = STATE_RULE[start], b = STATE_RULE[target];
    const ops: StepSwitch[] = [];
    meta.barSeps.forEach((bs) => {
        const from = wantBarSepClosed(id, bs.bar, a), to = wantBarSepClosed(id, bs.bar, b);
        if (from !== to) ops.push({
            type: "barSep", bar: bs.bar, barLine: sepBarLine[`${bs.cell[0]}_${bs.cell[1]}`],
            cell: bs.cell, action: to ? "close" : "open",
        });
    });
    if (meta.lineSep && (a.lineSep === "closed") !== (b.lineSep === "closed"))
        ops.push({ type: "lineSep", cell: meta.lineSep, action: b.lineSep === "closed" ? "close" : "open" });
    if (meta.breaker && (a.breaker === "closed") !== (b.breaker === "closed"))
        ops.push({ type: "breaker", cell: meta.breaker, action: b.breaker === "closed" ? "close" : "open" });
    if (meta.clp && (a.clp === "closed") !== (b.clp === "closed"))
        ops.push({ type: "clp", cell: meta.clp, action: b.clp === "closed" ? "close" : "open" });
    if (meta.generator && wantGenOn(a) !== wantGenOn(b))
        ops.push({ type: "generator", cell: meta.generator, action: wantGenOn(b) ? "start" : "stop" });
    ops.sort((p, q) => (OP_WEIGHT[`${p.type}:${p.action}`] ?? 99) - (OP_WEIGHT[`${q.type}:${q.action}`] ?? 99));
    return ops;
};

/**
 * Builds the full dictated program for an exercise.
 *
 * `cellMeta` / `sepBarLine` come from the painted layout (Functions/srsScheme),
 * so the program only ever asks for switches that physically exist on the bay.
 */
export function buildProgram(
    initialId: string, finalId: string, selection: string,
    cellMeta: Record<string, CellSwitches>, sepBarLine: Record<string, string>,
): LearnProgram {
    const startClass = stateClassOf(initialId);

    const steps: LearnStep[] = [];
    const rows: FileRow[] = [];
    let group = 0;

    // The exercise walks the operative-state ladder one rung at a time (never
    // straight from "in functiune" to "separat vizibil") and then climbs back to
    // the state it started from.
    const journey = stateJourney(initialId, finalId);

    journey.slice(0, -1).forEach((legFrom, i) => {
        const legTo = journey[i + 1];
        const cfg = buildConfig(legFrom, legTo, selection);
        const legToClass = stateClassOf(legTo);
        // De-energizing = the scope moves further out of service.
        const deEnergizing = stateDepthOf(legTo) > stateDepthOf(legFrom);

        // Cells that actually move on this leg, in the order they must be worked on:
        //   - a cell that moves the OTHER way is preparatory (e.g. the opposite
        //     transversal coupling that keeps the rest of the station fed while a
        //     semistation is dropped) and goes first
        //   - de-energizing: consumers, then the generators, then the measuring
        //     cell whose CLP earths the busbar (it may only be closed once every bar
        //     separator on that busbar is already open)
        //   - energizing: exactly the reverse
        const direction = (id: string) => STATE_DEPTH[cfg.target[id]] - STATE_DEPTH[cfg.start[id]];
        const rank = (id: string): number => {
            const d = direction(id);
            if (d !== 0 && (d > 0) !== deEnergizing) return -1;   // preparatory maneuver
            if (SRS_CELLS.find((c) => c.id === id)?.isMeasurement) return 2;
            return GENERATOR_IDS.has(id) ? 1 : 0;
        };
        const moving = SRS_CELLS
            .filter((c) => cellMeta[c.id] && cfg.start[c.id] !== cfg.target[c.id])
            .map((c) => c.id)
            .sort((a, b) => {
                const ra = rank(a), rb = rank(b);
                if (ra !== rb) return deEnergizing || ra < 0 || rb < 0 ? ra - rb : rb - ra;
                return 0;
            });

        moving.forEach((id) => {
            const ops = cellOperations(id, cellMeta[id], cfg.start[id], cfg.target[id], sepBarLine);
            if (!ops.length) return;
            group++;
            const stepId = String(group);
            // The state this cell has to reach on this leg. It is usually the leg's
            // own state, but a cell can be given a shallower target inside a wider
            // scope (only the measuring cell is earthed when a whole busbar is), and
            // then it is named after its own state class.
            const state = (cfg.target[id] === legToClass ? stateLabelOf(legTo) : STATE_LABEL[cfg.target[id]]).toLowerCase();

            // One dictated order per cell: "Adu in stare deconectata LEA SRA OE1, celula 4K."
            steps.push({
                id: stepId,
                number: stepId,
                kind: "order",
                cellId: id,
                ops,
                dispatcher: `Adu în ${state} ${at(id)}.`,
                confirm: `Am înțeles, aduc în ${state} ${at(id)}.`,
                report: `S-a adus în ${state} ${at(id)}.`,
                ack: `Am înțeles, s-a adus în ${state} ${at(id)}.`,
                fileText: `La dispoziția D.E.I. se aduce în ${state} ${at(id)} prin:`,
                legIdx: i,
                legFrom: stateLabelOf(legFrom),
                legTo: stateLabelOf(legTo),
            });
            // The sheet keeps the full detail: the order, then every switch operation
            // with the position check that follows it.
            rows.push({ number: stepId, text: steps[steps.length - 1].fileText, stepId, header: true });
            let sub = 0;
            ops.forEach((sw, opIdx) => {
                sub++;
                rows.push({
                    number: `${stepId}.${sub}`, stepId, opIdx, rowId: `${stepId}.${sub}`,
                    text: `Se identifică și ${verbFile(sw)} ${nounOf(sw)} de la ${at(id)}.`,
                });
                sub++;
                rows.push({
                    number: `${stepId}.${sub}`, stepId, opIdx, rowId: `${stepId}.${sub}`,
                    text: `Se verifică poziția ${positionOf(sw)} a ${nounOfGen(sw)} de la ${at(id)}.`,
                });
            });
        });
    });

    // Closing announcement.
    if (steps.length) {
        group++;
        const id = String(group);
        steps.push({
            id,
            number: id,
            kind: "announce",
            ops: [],
            dispatcher: "Anunță D.E.I. de terminarea manevrei.",
            confirm: "Anunț D.E.I. de terminarea manevrei.",
            fileText: "Se anunță D.E.I. de terminarea manevrei.",
            // Belongs to no leg: it closes the exercise, after the last one.
            legIdx: -1,
            legFrom: "",
            legTo: "",
        });
        rows.push({ number: id, text: "Se anunță D.E.I. de terminarea manevrei.", stepId: id, rowId: id });
    }

    return {
        steps,
        rows,
        theme: `Aducerea în ${stateLabelOf(finalId).toLowerCase()} a ${scopeLabel(selection)}`
            + ` și revenirea în ${stateLabelOf(initialId).toLowerCase()}`,
        purpose: `Revizie la ${scopeLabel(selection)}`,
        initialNote: startClass === "NORMAL"
            ? "Conform schemei normale de funcționare."
            : `Conform schemei în ${stateLabelOf(initialId).toLowerCase()}.`,
    };
}
